import {
	FILE_TYPE_EXPORT_EXCEL,
	MasterInfoEnum,
	TextMessage,
	TimeOptionEnum,
} from '@/constant';
import { useCommonList, useFormikCustom } from '@/hooks';
import {
	getWorkerDashboardExcelList,
	IProgram,
	TApplicantDashboardExcelResponse,
	TEmployerDashboardExcelResponse,
	TSystemUnitItem,
} from '@/services';
import { CommonSelectors } from '@/stores';
import {
	applyCellStyleAlignmentCenter,
	dayjs,
	formatDate,
	showAlert,
	SUPPORT_FORMAT_DATE_LIST,
} from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { DatePickerProps, RangePickerProps } from 'antd/es/date-picker';

import type { RefSelectProps } from 'antd';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import {
	addDropdownValidation,
	applyCellFillColor,
	DROP_DOWN_VALIDATION_OPTIONS_CROP_TYPE,
	DROP_DOWN_VALIDATION_OPTIONS_GENDER,
	DROP_DOWN_VALIDATION_OPTIONS_VISA_CODE,
	initialValues,
	sortApplicantData,
	sortEmployerData,
	validationSchema,
} from './config';
import { IProps, TApplicantData, TEmployerData } from './type';

export const useLogic = (props: IProps) => {
	const [programList, setProgramList] = useState<IProgram[]>([]);
	const [isInitialized, setIsInitialized] = useState(false);

	const formik = useFormikCustom({
		initialValues,
		onSubmit: (values) => {
			//
		},
		validationSchema,
	});

	const [loading, setLoading] = useState<boolean>(false);

	const selectRef = useRef<RefSelectProps>(null);
	const { selectMasterInfo: programListOptions, commonList } = useCommonList(
		MasterInfoEnum.PROGRAM
	);
	const headerProgram = useSelector(CommonSelectors.selectHeaderProgram);

	useEffect(() => {
		setProgramList((commonList?.data as IProgram[]) || []);
	}, [commonList?.data]);

	useEffect(() => {
		if (headerProgram.value && !isInitialized) {
			formik.setValues((prevValues) => ({
				...prevValues,
				programIds: [Number(headerProgram.value)],
			}));
			setIsInitialized(true);
		}
	}, [headerProgram.value, isInitialized]);

	const selectedPrograms = useMemo(() => {
		return formik.values.programIds.map((id: number) => {
			const program = programList?.find(
				(program) => Number(program.id) === Number(id)
			);
			return program;
		});
	}, [formik.values.programIds, programList]);

	const handleSelectTimeChange = useCallback(
		(value: TimeOptionEnum) => {
			const today = dayjs();
			switch (value) {
				case TimeOptionEnum.TODAY:
					formik.setValues({
						...formik.values,
						startDate: formatDate(today),
						endDate: formatDate(today),
						selectDate: value,
					});
					break;

				case TimeOptionEnum.THIS_WEEK:
					formik.setValues({
						...formik.values,
						selectDate: value,
						startDate: formatDate(today.startOf('week')),
						endDate: formatDate(today.endOf('week')),
					});
					break;
				case TimeOptionEnum.THIS_MONTH:
					formik.setValues({
						...formik.values,
						selectDate: value,
						startDate: formatDate(today.startOf('month')),
						endDate: formatDate(today.endOf('month')),
					});
					break;
				case TimeOptionEnum.THIS_YEAR:
					formik.setValues({
						...formik.values,
						selectDate: value,

						startDate: formatDate(today.startOf('year')),
						endDate: formatDate(today.endOf('year')),
					});
					break;
				default:
					formik.setValues({
						...formik.values,
						selectDate: '',
						startDate: formatDate(dayjs().subtract(1, 'year')),
						endDate: formatDate(dayjs()),
					});
					break;
			}
		},
		[formik.values]
	);

	const handleRemoveProgram = useCallback(
		(selectId: number) => {
			formik.setValues({
				...formik.values,
				programIds: formik.values.programIds.filter(
					(id: number) => id !== selectId
				),
			});
		},
		[formik.values]
	);

	const getFileName = useCallback(() => {
		const fileName = '프로그램 운영현황';

		let startDate = '';
		let endDate = '';
		if (!selectedPrograms.length) return fileName;

		if (formik.values.startDate && formik.values.endDate) {
			startDate = formatDate(formik.values.startDate);
			endDate = formatDate(formik.values.endDate);
		} else if (formik.values.startDate) {
			startDate = formatDate(formik.values.startDate);
		} else if (formik.values.endDate) {
			endDate = formatDate(formik.values.endDate);
		} else {
			if (selectedPrograms.length === 1) {
				// have 1 program
				startDate = formatDate(selectedPrograms[0]?.startDate);
				endDate = formatDate(selectedPrograms[0]?.endDate);
			} else if (selectedPrograms.length > 1) {
				// have many program
				let minStartDate = dayjs(
					selectedPrograms[0]?.startDate,
					SUPPORT_FORMAT_DATE_LIST
				);
				let maxEndDate = dayjs(
					selectedPrograms[0]?.endDate,
					SUPPORT_FORMAT_DATE_LIST
				);

				selectedPrograms.forEach((program) => {
					const programStartDate = dayjs(
						program?.startDate,
						SUPPORT_FORMAT_DATE_LIST
					);
					const programEndDate = dayjs(
						program?.endDate,
						SUPPORT_FORMAT_DATE_LIST
					);

					minStartDate =
						dayjs.min(minStartDate, programStartDate) || minStartDate;
					maxEndDate = dayjs.max(maxEndDate, programEndDate) || maxEndDate;
				});

				startDate = formatDate(minStartDate);
				endDate = formatDate(maxEndDate);
			}
		}
		return `${fileName} (${startDate} ~ ${endDate})`;
	}, [formik.values.startDate, formik.values.endDate, selectedPrograms]);

	// prettier-ignore
	const onDownload = useCallback(async (reason: string, callback?: () => void) => {
		await formik.submitForm();
		const error = await formik.validateForm(formik.values);
		if (isNotNilOrEmpty(error)) {
			showAlert(TextMessage.ALERT_12);
			return;
		}
		const startRowData = 4;
		try {
			setLoading(true);
			const fileName = getFileName();
			const fileNameWithExtension = `${fileName}.xlsx`;
			const templateName = 'template_current-situation-excel';
			const template = await fetch(
				`${process.env.PUBLIC_URL}/template-excel/${templateName}.xlsx`
			);
			if (!template.ok) {
				throw new Error('Failed to fetch the template');
			}
			const arrayBuffer = await template.arrayBuffer();
			const workbook = new ExcelJS.Workbook();
			await workbook.xlsx.load(arrayBuffer);
			const worksheetFranchise = workbook.getWorksheet(1);
			const worksheetApplicant = workbook.getWorksheet(3);

			const response = await getWorkerDashboardExcelList({
				programIds: formik.values.programIds,
				startDate: formatDate(formik.values.startDate),
				endDate: formatDate(formik.values.endDate),
				downloadReason: reason,
				downloadFileName: fileNameWithExtension	,
			});

			if (response?.status !== 200) {
				throw new Error('Failed to fetch the data');
			}

			const franchiseData: TEmployerDashboardExcelResponse[] =
				response?.data?.franchises;
			const applicant: TApplicantDashboardExcelResponse[] =
				response?.data?.applicants;
			const unitData: TSystemUnitItem = response?.data?.unit;
			let employerData: TEmployerData[] = [];
			let applicantData: TApplicantData[] = [];

			const countyDistrict = unitData?.countyDistrict;

			if (franchiseData?.length > 0) {
				employerData = franchiseData.map(
					(franchise: TEmployerDashboardExcelResponse) => {
						return {
							...franchise,
							cityProvince: unitData?.cityProvince,
							countyDistrict: unitData?.countyDistrict,
						};
					}
				);
			}

			if (applicant?.length > 0) {
				applicantData = applicant.map(
					(applicant: TApplicantDashboardExcelResponse) => {
						return {
							...applicant,
							cityProvince: unitData?.cityProvince,
							countyDistrict: unitData?.countyDistrict,
						};
					}
				);
			}

			employerData?.forEach((employer: TEmployerData, index: number) => {
				const excelRow = worksheetFranchise?.getRow(index + startRowData);
				if (!excelRow) return;

				// insert index
				const firstCell = excelRow.getCell(1);
				firstCell.value = index + 1;
				applyCellStyleAlignmentCenter(firstCell);

				Object.entries(sortEmployerData(employer)).forEach(
					([key, value], colIndex) => {
						const cell = excelRow.getCell(colIndex + 2);
						if (cell) {
							cell.value = value;
							applyCellStyleAlignmentCenter(cell);

							if (key === 'productionType') {
								addDropdownValidation(
									cell,
									DROP_DOWN_VALIDATION_OPTIONS_CROP_TYPE
								);
							}
						}

						excelRow.getCell(colIndex + 2).value = value; // Start filling from the second column
					}
				);
			});

			applicantData?.forEach((applicant: TApplicantData, index: number) => {
				const excelRow = worksheetApplicant?.getRow(index + startRowData);
				if (!excelRow) return;

				// insert index
				const firstCell = excelRow.getCell(1);
				firstCell.value = index + 1;
				applyCellStyleAlignmentCenter(firstCell);

				Object.entries(sortApplicantData(applicant)).forEach(
					([key, value], colIndex) => {
						const cell = excelRow.getCell(colIndex + 2);
						if (cell) {
							cell.value = value;
							applyCellStyleAlignmentCenter(cell);

							if (
								key === 'entryDate' ||
								key === 'unauthorizedDepartureDate' ||
								key === 'departureDate'
							) {
								applyCellFillColor(cell, key);
								if (index === applicantData.length - 1) {
									cell.style = {
										...cell.style,
										border: {
											...cell.style?.border,
											bottom: { style: 'medium' },
										},
									};
								}
							}

							if (
								key === 'unauthorizedDepartureDate' ||
								key === 'departureDate'
							) {
								cell.style = {
									...cell.style,
									font: {
										color: { argb: 'FF0000' },
									},
								};
							}
							if (key === 'gender') {
								addDropdownValidation(
									cell,
									DROP_DOWN_VALIDATION_OPTIONS_GENDER
								);
							}
							if (key === 'visaCode') {
								addDropdownValidation(
									cell,
									DROP_DOWN_VALIDATION_OPTIONS_VISA_CODE
								);
							}
						}
						excelRow.getCell(colIndex + 2).value = value; // Start filling from the second column
					}
				);
			});

			const titleCellSheet1 = worksheetFranchise?.getCell(1, 1);
			if (!titleCellSheet1) return;
			titleCellSheet1.value = `계절근로자 배정 고용주 현황${countyDistrict ? `(${countyDistrict})` : ''}`;

			const titleCellSheet3 = worksheetApplicant?.getCell(1, 1);
			if (!titleCellSheet3) return;
			titleCellSheet3.value = `계절근로자 입국 / 이탈 / 출국 현황${countyDistrict ? `(${countyDistrict})` : ''}`;

			// Save the filled template
			const bufferOutput = await workbook.xlsx.writeBuffer();
			const blob = new Blob([bufferOutput], {
				type: FILE_TYPE_EXPORT_EXCEL,
			});

			saveAs(blob, `${fileName}.xlsx`);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
			callback?.();
			props.onCancel();
		}
	}, [formik.values, getFileName, props.onCancel]);

	const disabledStartDate: RangePickerProps['disabledDate'] = (current) => {
		return (
			!!formik.values.endDate &&
			current &&
			current >
				dayjs(formik.values.endDate, SUPPORT_FORMAT_DATE_LIST).endOf('day')
		);
	};

	const disabledEndDate: RangePickerProps['disabledDate'] = (current) => {
		return (
			!!formik.values.startDate &&
			current &&
			current < dayjs(formik.values.startDate, SUPPORT_FORMAT_DATE_LIST)
		);
	};

	const handleDateChange = useCallback(
		(value: DatePickerProps['value'], type: 'start' | 'end') => {
			formik.setValues({
				...formik.values,
				selectDate: '',
				[type === 'start' ? 'startDate' : 'endDate']: value,
			});
		},
		[formik.values]
	);

	const handleSelectProgram = () => {
		selectRef.current?.blur();
	};

	return {
		loading,
		formik,
		onDownload,
		handleSelectTimeChange,
		selectedPrograms,
		handleRemoveProgram,
		disabledStartDate,
		disabledEndDate,
		handleDateChange,
		selectRef,
		handleSelectProgram,
		programListOptions,
	};
};
